import Api from '../util/Api';
import { HttpError } from '../services/http-error';
import type { ApiResponse } from '../types/api';
import type { Media } from '../types/media';

/**
 * The reschedule routes (ROBO-612 / ROBO-616), as plain functions — `use-reschedule.ts` wraps them
 * in react-query. They replace `Dashboard/reschedule.asp`'s read and its `?nUpdate=1` write.
 *
 *   GET  orders/{orderId}/tasks/{taskId}/reschedule               the page's view of one task
 *   POST orders/{orderId}/tasks/{taskId}/reschedule               schedule / TBD / cancel / preference
 *   GET  orders/{orderId}/tasks/{taskId}/reschedule/appointments  the mobile app's post-save summary
 *   GET  orders/{orderId}/tasks/{taskId}/reschedule/team          who may be chosen instead (ROBO-694)
 *
 * ★ ONE RULE DECIDES HOW EVERY CALL IS SENT. A GUEST — no session, the site's default download
 * guid from the `/r/` link — sends `hd-download-guid` and NO bearer token (`skipAuth`, so `Api`
 * never goes looking for a session and never reports "could not authorize api"). A SIGNED-IN
 * caller sends their token and no guid: the API uses the session when it has one, and a guid sent
 * beside it would only be a second, unused credential on the wire.
 *
 * ★ EVERYTHING UNREACHABLE IS THE SAME 404. Wrong id, someone else's task, a completed task, a bad
 * or missing guid — the API does not say which, and neither may the page.
 */

export type RescheduleCalendarModule = 'cart-gcal' | 'cart-apptpref';
export type RescheduleCallerKind = 'client' | 'guest' | 'staff';
export type RescheduleMode = 'schedule' | 'tbd' | 'cancel' | 'preference';

export interface RescheduleSite {
    siteId: number;
    address?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    latitude?: number | null;
    longitude?: number | null;
}

/**
 * The brand's appointment-preference calendar (ROBO-698) — the design's `cart-apptpref.*`
 * variables, with legacy's defaults already applied by the API. Sent only when `calendarModule` is
 * `cart-apptpref`. Every key is optional here because the API omits what it has not got; the
 * `cart-apptpref` module's `normalizePreferenceSettings` fills the gaps the same way legacy does.
 */
export interface ReschedulePreference {
    /** The ORDER brand's local date, `YYYY-MM-DD` — not the visitor's and not the web server's. */
    today: string;
    /**
     * Slot names in order (`slot1`…`slotN`), raw from the design: a name may be empty or hold
     * markup. Shown as text, saved untouched. None = the free-text input instead.
     */
    slots?: string[];
    weeks?: number;
    weeksMax?: number;
    daysOut?: number;
    minSelections?: number;
    maxSelections?: number;
    /** 1 = Sunday … 7 = Saturday. */
    daysOfWeek?: number[];
    /** Closed dates, each `YYYY-MM-DD`. */
    unavailable?: string[];
}

export interface RescheduleTask {
    taskId: number;
    orderId: number;
    brandId: number;
    site: RescheduleSite;
    /** Wall clock in the brand's time, `YYYY-MM-DD HH:mm:ss`, no zone. Null when not scheduled. */
    shootDate: string | null;
    tbd: boolean;
    teamMemberId: number | null;
    teamMemberName?: string | null;
    teamMemberPhotoMediaId?: number | null;
    /** The current photographer's photo, in the shape a team-members roster row carries. */
    teamMemberMedia?: Media;
    products: string[];
    /**
     * ROBO-854: the drone products moving with this appointment (flagged drone, or in a flagged package).
     * When any, the page checks the FAA airspace for the new date. Absent on an API older than ROBO-854.
     */
    droneProducts?: string[];
    /**
     * Every task that moves with this one (this task included) — proc pass 1's group. These are
     * what availability and a hold must treat as not busy, or the appointment blocks its own new
     * time. Absent on an API older than the ROBO-616 slice; `movingTaskIdsOf` covers that.
     */
    movingTaskIds?: number[];
    /** Minutes on site across the tasks that move together; `-1` is twilight / date-only. */
    totalTime: number;
    /** Which scheduling module the brand's cart uses; null means only TBD / Cancel are offered. */
    calendarModule: RescheduleCalendarModule | null;
    /**
     * ROBO-698: the slot calendar's settings, when `calendarModule` is `cart-apptpref`. Absent on
     * an API older than the ROBO-698 slice, and the page then keeps its free-text input.
     */
    preference?: ReschedulePreference;
    callerKind: RescheduleCallerKind;
    zeroTime: boolean;
    tooLate: boolean;
    canCancel: boolean;
    canTbd: boolean;
    /** True exactly when the team route would answer a `chooser` other than `'none'`. */
    canChoosePhotographer: boolean;
    /**
     * ROBO-694: the current photographer cannot do the appointment's product, so the chooser is
     * open from the start — for every caller kind (legacy `reschedule.asp` :454-462). Absent on an
     * API older than the ROBO-694 slice.
     */
    autoOpenPhotographers?: boolean;
    travelFeeEnabled: boolean;
    /**
     * ROBO-865: the ORDER's calendar module has the weather off (`disableweather`). Weather is on by
     * default; absent on an API older than ROBO-865, which reads as "on".
     */
    weatherDisabled?: boolean;
    /** The same module's `weatherprecip`. Absent on an older API, which reads as the default. */
    weatherPrecipThreshold?: number;
}

/**
 * How the photographer may be changed (ROBO-694):
 * - `cards`          one card per photographer (legacy `cart-team`, mode 1 or unset);
 * - `team-calendar`  the brand hides its team (`cart-team.mode` 2): ONE unnamed merged calendar,
 *                    the photographer resolved from the slot that is picked;
 * - `none`           the caller may not choose.
 */
export type RescheduleTeamChooser = 'cards' | 'team-calendar' | 'none';

/**
 * One offered photographer. The server has already applied every rule that decides who is here.
 *
 * ★ A VALUE THE API HAS NOT GOT IS AN ABSENT KEY, NEVER `null`: `name`, `photoMediaId`, `media`,
 * `travelFee` and `travelFeePay` can all be missing.
 */
export interface RescheduleTeamMember {
    id: number;
    /** Absent when `chooser` is `team-calendar`: nobody is named. */
    name?: string;
    photoMediaId?: number;
    /**
     * The member's photo, exactly as a team-members roster row carries it. Absent with no photo
     * and when `chooser` is `team-calendar`.
     */
    media?: Media;
    favorite: boolean;
    recentlyUsed: boolean;
    priority: number;
    distance: number;
    /** False only for staff: a client or guest is never sent an out-of-area member. */
    inArea: boolean;
    /** Always true for staff (legacy's override mode); `inArea: false` is their warning flag. */
    available: boolean;
    weekMask: number;
    /**
     * Absent unless the order's brand charges travel fees on a reschedule; then a number for
     * every member, 0 for none.
     */
    travelFee?: number;
    /** The member's payout — staff callers only; always absent for a client or guest. */
    travelFeePay?: number;
}

export interface RescheduleTeam {
    /** `cards` with no members is the empty state: the caller may choose, but nobody is offered. */
    chooser: RescheduleTeamChooser;
    /** The same fact as the view's `autoOpenPhotographers`. */
    autoOpen: boolean;
    /** Whether "Any Available" is offered. False unless `chooser` is `cards`. */
    anyAvailable: boolean;
    /** Design var `cart-gcal.team`: the `nTeam` a team calendar is asked with. */
    teamMode: 0 | 1 | 2;
    /** Design var `cart-gcal.teamselect`: how a team-calendar slot picks its member. */
    teamSelect: 1 | 2 | 3;
    displayLimit: number;
    /** In the legacy SQL order. Sorting, the limit and the default are the `cart-team` module's. */
    members: RescheduleTeamMember[];
}

export interface RescheduleRequest {
    mode: RescheduleMode;
    /** `YYYY-MM-DD HH:mm:ss`, the brand's wall clock. `schedule` only. */
    shootDate?: string;
    teamMemberId?: number;
    appointmentPreferences?: string;
    comment?: string;
    holdIds?: string[];
    source?: string;
    /** "Any Available" was the choice; `teamMemberId` is then the member the slot resolved to. */
    teamAny?: boolean;
    /**
     * STAFF ONLY, `schedule` only (ROBO-694). The chosen member's fee and payout, as legacy's
     * `nTF` / `nTFP`. A client or guest never sends them: the server computes theirs and ignores
     * anything in the body.
     */
    travelFee?: number;
    travelFeePay?: number;
}

/** One row per group of tasks that moved together. */
export interface RescheduleGroup {
    orderId: number;
    taskIds: number[];
    teamMemberId: number | null;
    previousTeamMemberId: number | null;
    shootDate: string | null;
    previousShootDate: string | null;
    tbd: boolean;
    cancel: boolean;
}

/** Legacy's `for json auto` row (`reschedule.asp` :113-128), passed to the mobile app untouched. */
export interface RescheduleAppointment {
    month?: number;
    day?: number;
    time?: string;
    sort?: number;
    duration?: number;
    provider?: string;
    tbd?: number | boolean;
    future?: number;
    reschedule?: number;
}

export interface RescheduleAppointments {
    siteId: number;
    appointments: RescheduleAppointment[];
}

/** Who is calling. `downloadGuid` set means a guest; absent means the signed-in session. */
export interface RescheduleCaller {
    downloadGuid?: string;
}

/** The tasks being moved, falling back to the requested task alone when the API does not say. */
export function movingTaskIdsOf(task: Pick<RescheduleTask, 'taskId' | 'movingTaskIds'>): number[] {
    return Array.isArray(task.movingTaskIds) && task.movingTaskIds.length > 0
        ? task.movingTaskIds
        : [task.taskId];
}

export const reschedulePath = (orderId: number, taskId: number) =>
    `orders/${orderId}/tasks/${taskId}/reschedule`;

function callerArgs(caller: RescheduleCaller) {
    const guid = caller.downloadGuid;
    return {
        skipAuth: Boolean(guid),
        opts: guid ? { headers: { 'hd-download-guid': guid } } : undefined
    };
}

/** `AnnotatedResults<T>` serializes its payload as `data`; older/bypassed answers use `Data`. */
function unwrap<T>(response: ApiResponse<unknown>): T | undefined {
    const record = response as unknown as Record<string, unknown>;
    return (record.data ?? record.Data) as T | undefined;
}

/**
 * `Api.fetch` resolves on failure with `status: 100`; the real status and the API's message are on
 * the `HttpError` it kept. Thrown as-is so callers can branch on `error.status` (404 / 400 / 409).
 */
function assertOk(response: ApiResponse<unknown>, fallbackMessage: string): void {
    if (!response.ok) {
        throw response.error ?? new Error(response.statusText || fallbackMessage);
    }
}

/** The HTTP status of a failed reschedule call, when the server answered at all. */
export function rescheduleErrorStatus(error: unknown): number | undefined {
    return error instanceof HttpError ? error.status : undefined;
}

export async function fetchRescheduleTask(
    orderId: number,
    taskId: number,
    caller: RescheduleCaller
): Promise<RescheduleTask> {
    const { skipAuth, opts } = callerArgs(caller);
    const response = await Api.get<undefined, unknown>(
        reschedulePath(orderId, taskId),
        undefined,
        skipAuth,
        opts
    );
    assertOk(response, 'Unable to load this appointment');
    const task = unwrap<RescheduleTask>(response);
    if (!task) throw new Error('Unable to load this appointment');
    return task;
}

export async function saveReschedule(
    orderId: number,
    taskId: number,
    body: RescheduleRequest,
    caller: RescheduleCaller
): Promise<RescheduleGroup[]> {
    const { skipAuth, opts } = callerArgs(caller);
    const response = await Api.post<RescheduleRequest, unknown>(
        reschedulePath(orderId, taskId),
        body,
        skipAuth,
        opts
    );
    assertOk(response, 'Unable to save your changes');
    const groups = unwrap<RescheduleGroup[]>(response);
    return Array.isArray(groups) ? groups : [];
}

/**
 * Who may be chosen instead of the current photographer (ROBO-694). Same reach rules and the same
 * uniform 404 as the task read. Call it only when the task says `canChoosePhotographer`.
 */
export async function fetchRescheduleTeam(
    orderId: number,
    taskId: number,
    caller: RescheduleCaller
): Promise<RescheduleTeam> {
    const { skipAuth, opts } = callerArgs(caller);
    const response = await Api.get<undefined, unknown>(
        `${reschedulePath(orderId, taskId)}/team`,
        undefined,
        skipAuth,
        opts
    );
    assertOk(response, 'Unable to load the photographers');
    const team = unwrap<RescheduleTeam>(response);
    if (!team) throw new Error('Unable to load the photographers');
    return { ...team, members: Array.isArray(team.members) ? team.members : [] };
}

export async function fetchRescheduleAppointments(
    orderId: number,
    taskId: number,
    caller: RescheduleCaller
): Promise<RescheduleAppointments> {
    const { skipAuth, opts } = callerArgs(caller);
    const response = await Api.get<undefined, unknown>(
        `${reschedulePath(orderId, taskId)}/appointments`,
        undefined,
        skipAuth,
        opts
    );
    assertOk(response, 'Unable to load the appointment summary');
    const summary = unwrap<RescheduleAppointments>(response);
    if (!summary) throw new Error('Unable to load the appointment summary');
    return summary;
}
